import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Mail } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If that email is registered, a reset link is on the way");
    } catch {
      toast.error("Could not send reset email — please try again");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8" data-testid="forgot-logo-link">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-purple-400 to-purple-600 grid place-items-center"><ShieldCheck size={18} className="text-white"/></div>
          <span className="font-semibold text-slate-900 text-lg">Informed <span className="text-purple-600">Accelerator</span></span>
        </Link>
        <Card className="glass rounded-2xl">
          <CardContent className="p-8">
            <div className="eyebrow">Recover access</div>
            <h1 className="text-2xl font-semibold text-slate-900 mt-1">Forgot your password?</h1>
            {!sent ? (
              <>
                <p className="text-sm text-slate-500 mt-1">Enter your account email and we'll send you a secure reset link. The link expires in 30 minutes.</p>
                <form className="mt-6 space-y-4" onSubmit={submit}>
                  <div>
                    <Label className="text-slate-700">Email</Label>
                    <Input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="forgot-email-input" className="mt-1 bg-slate-50 border-slate-200 text-slate-900"/>
                  </div>
                  <Button type="submit" disabled={loading} data-testid="forgot-submit-button" className="btn-primary rounded-xl w-full py-6">
                    {loading ? "Sending…" : "Send reset link"}
                  </Button>
                </form>
              </>
            ) : (
              <div className="mt-4 rounded-xl bg-purple-500/10 border border-purple-400/30 p-5 text-slate-800" data-testid="forgot-sent-panel">
                <div className="flex items-center gap-2 text-slate-900 font-semibold"><Mail size={16} className="text-purple-700"/> Check your inbox</div>
                <p className="text-sm text-slate-700 mt-2">If <span className="text-slate-900">{email}</span> is registered, we've sent a reset link that expires in 30 minutes. Check your spam folder if you don't see it.</p>
              </div>
            )}
            <div className="mt-6 text-sm text-slate-500 text-center">
              <Link to="/login" className="text-purple-600 hover:underline" data-testid="forgot-back-link">Back to sign in</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
